import { Phone, Mail, MapPin, Clock, MessageSquare } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";

import PageHero from "@/components/PageHero";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/site";

const HERO_IMG =
  "/images/d0l7uee1.png";

export default function Contact() {
  const contactBlocks = [
    {
      icon: Phone,
      label: "Phone",
      value: COMPANY.phone,
      href: `tel:${COMPANY.phoneRaw}`,
      testid: "contact-phone",
    },
    {
      icon: WhatsAppIcon,
      label: "WhatsApp",
      value: `Chat ${COMPANY.phone}`,
      href: COMPANY.whatsappHref,
      external: true,
      testid: "contact-whatsapp",
    },
    {
      icon: MessageSquare,
      label: "Text / SMS",
      value: `Text ${COMPANY.phone}`,
      href: COMPANY.smsHref,
      testid: "contact-text",
    },
    {
      icon: Mail,
      label: "Email",
      value: COMPANY.email,
      href: `mailto:${COMPANY.email}`,
      testid: "contact-email",
    },
    {
      icon: MapPin,
      label: "Service Area",
      value: COMPANY.serviceArea,
      testid: "contact-area",
    },
    {
      icon: Clock,
      label: "Hours",
      value: COMPANY.hours,
      testid: "contact-hours",
    },
  ];

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact"
        description="Talk to a Vision Era Construction estimator. Phone, WhatsApp, text and email — serving the Greater Toronto Area."
        path="/contact"
      />
      <PageHero
        eyebrow="Talk to a real estimator"
        title="Start your project."
        subtitle="Send your specs, photos, or rough requirements. A senior estimator replies inside one business day — no call-center, no upsell."
        image={HERO_IMG}
      />

      <section className="section-pad">
        <div className="container-max grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Direct Lines</div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-wider text-zinc-50 mt-4 leading-[0.95]">
              Reach us
              <br />
              the fastest way.
            </h2>
            <div className="grid grid-cols-1 gap-px bg-zinc-800 border border-zinc-800 mt-10">
              {contactBlocks.map((b) => {
                const Icon = b.icon;
                const inner = (
                  <div className="flex items-start gap-5 p-6 bg-zinc-950 group">
                    <div className="w-11 h-11 grid place-items-center bg-[#FF5A00]/10 border border-[#FF5A00]/40 shrink-0">
                      <Icon className="w-5 h-5 text-[#FF5A00]" />
                    </div>
                    <div>
                      <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                        {b.label}
                      </div>
                      <div className="text-zinc-100 mt-1 group-hover:text-[#FF5A00] transition-colors">
                        {b.value}
                      </div>
                    </div>
                  </div>
                );
                return b.href ? (
                  <a
                    key={b.label}
                    href={b.href}
                    data-testid={b.testid}
                    target={b.external ? "_blank" : undefined}
                    rel={b.external ? "noopener noreferrer" : undefined}
                    className="block"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={b.label} data-testid={b.testid}>
                    {inner}
                  </div>
                );
              })}
            </div>

            <div className="border border-zinc-800 bg-zinc-900 p-8 mt-8">
              <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#FF5A00]">
                Coverage
              </div>
              <h3 className="font-display text-2xl tracking-wider uppercase text-zinc-50 mt-2">
                Greater Toronto Area
              </h3>
              <p className="text-zinc-400 mt-3 leading-relaxed text-sm">
                Toronto · Mississauga · Brampton · Vaughan · Markham · Richmond
                Hill · Oakville · Burlington · Pickering · Ajax · Whitby ·
                Oshawa. Project outside the GTA? Send the brief — we travel for
                the right job.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <QuoteForm />
          </div>
        </div>
      </section>
    </div>
  );
}
